import { TicketIcon, XIcon } from '@phosphor-icons/react';
import { Skeleton } from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/common/button';
import { Chip } from '@/components/common/chip';
import { UserAvatar } from '@/components/common/user-avatar';
import { Drawer } from '@/components/feedback/drawer';
import { ImageLightbox } from '@/components/feedback/image-lightbox';
import { routes } from '@/config/routes';
import { customerAvatar } from '@/lib/avatar';

import { formatReplacementDateTime, REPLACEMENT_STATUS_CHIP } from '../replacements/format';
import { useReplacement } from '../replacements/hooks';
import type { ReplacementDetail, ReplacementRow } from '../replacements/types';

import styles from './replacement-detail-drawer.module.scss';

export type ReplacementDetailDrawerProps = {
  open: boolean;
  id: string;
  /** The row the drawer was opened from — the real detail response carries no
   *  customer/order/date fields at all (it's item/claim-centric), so those come from the row
   *  the queue already fetched. */
  row: ReplacementRow | null;
  onClose: () => void;
  /** Wired in tickets 04/05; absent here — the Pending footer buttons still render (AC: reserved). */
  onApprove?: (detail: ReplacementDetail) => void;
  onReject?: (detail: ReplacementDetail) => void;
};

function HeaderTitle({
  orderNumber,
  status,
}: {
  orderNumber?: string;
  status?: ReplacementDetail['status'];
}) {
  const chip = status ? REPLACEMENT_STATUS_CHIP[status] : undefined;
  return (
    <span className={styles.header}>
      <span className={styles.iconBadge}>
        <TicketIcon size={20} weight="fill" aria-hidden />
      </span>
      <span className={styles.headerText}>
        <span className={styles.headerHeading}>Replacement Request</span>
        <span className={styles.headerSubtitleRow}>
          {orderNumber ? <span className={styles.headerSubtitle}>{orderNumber}</span> : null}
          {chip ? <Chip intent={chip.intent}>{chip.label}</Chip> : null}
        </span>
      </span>
    </span>
  );
}

function DetailBody({
  row,
  detail,
  isPending,
  onPreview,
}: {
  row: ReplacementRow;
  detail?: ReplacementDetail;
  isPending: boolean;
  onPreview: (url: string) => void;
}) {
  const navigate = useNavigate();
  if (isPending || !detail) return <Skeleton active paragraph={{ rows: 6 }} />;

  const requestedBy = detail.isRequestedByCustomer
    ? 'Customer'
    : detail.requestedBy
      ? `${detail.requestedBy.firstName} ${detail.requestedBy.lastName}`
      : '-';

  return (
    <>
      <div className={styles.infoCard}>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Customer Name</span>
          <span className={styles.infoValue}>
            <UserAvatar
              name={row.customerName}
              src={customerAvatar(row.customerName)}
              label={row.customerName}
              size={18}
            />
          </span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Phone Number</span>
          <span className={styles.infoValue}>{row.phone}</span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Order Date &amp; Time</span>
          <span className={styles.infoValue}>{formatReplacementDateTime(row.orderDate)}</span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>City</span>
          <span className={styles.infoValue}>{row.city}</span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Requested By</span>
          <span className={styles.infoValue}>{requestedBy}</span>
        </div>
      </div>

      {detail.status === 'approved' && detail.replacementOrderId ? (
        <div className={styles.orderBand}>
          <span className={styles.orderBandText}>
            <span className={styles.orderBandCaption}>Your replacement order has been created</span>
            <span className={styles.orderBandNumber}>{detail.replacementOrderNumber}</span>
          </span>
          <Button
            variant="neutral"
            size="small"
            onClick={() => navigate(routes.order(detail.replacementOrderId!))}
          >
            View Order
          </Button>
        </div>
      ) : null}

      <div className={styles.reasonCard}>
        <h3 className={styles.reasonHeading}>{detail.reasonLabel}</h3>
        {detail.description ? <p className={styles.description}>{detail.description}</p> : null}
      </div>

      <div className={styles.deviceHeader}>
        <span className={styles.deviceInfo}>
          <span className={styles.deviceName}>{detail.itemName}</span>
          <span className={styles.deviceSubtitle}>
            {detail.category ?? '-'} · Unit #{detail.unitIndex} · Qty {detail.quantity}
          </span>
        </span>
      </div>

      {detail.withInstallation ? (
        <div className={styles.infoCard}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Installer</span>
            <span className={styles.infoValue}>
              {detail.installerName ?? detail.externalInstallerName ?? '-'}
            </span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Installation On</span>
            <span className={styles.infoValue}>{detail.installationOn ?? '-'}</span>
          </div>
        </div>
      ) : null}

      {detail.images.length ? (
        <ul className={styles.thumbGrid}>
          {detail.images.map((img) =>
            img.url ? (
              <li key={img.id}>
                <button
                  type="button"
                  className={styles.thumbBtn}
                  aria-label={`View attachment ${img.originalFileName ?? img.id}`}
                  onClick={() => onPreview(img.url!)}
                >
                  <img className={styles.thumbImg} src={img.url} alt="" />
                </button>
              </li>
            ) : null,
          )}
        </ul>
      ) : null}

      {detail.status === 'rejected' && detail.rejectionReason ? (
        <div className={styles.rejection} role="alert">
          <span className={styles.rejectionHeading}>
            <XIcon size={18} aria-hidden />
            Rejection Reason
          </span>
          <p className={styles.rejectionValue}>{detail.rejectionReason}</p>
        </div>
      ) : null}
    </>
  );
}

/** Read-only replacement-request detail (ticket 03, ADR-0019; reconciled 114698). The real
 *  detail response is item/claim-centric — no customer/order/address fields — so this merges it
 *  with the `row` the queue already fetched for that context. `useReplacement` is id-gated →
 *  idle while closed. */
export function ReplacementDetailDrawer({
  open,
  id,
  row,
  onClose,
  onApprove,
  onReject,
}: ReplacementDetailDrawerProps) {
  const { data: detail, isPending } = useReplacement(open ? id : '');
  const [preview, setPreview] = useState<string | null>(null);
  const actionable = detail?.status === 'pending' ? detail : undefined;

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        width={549}
        title={
          <HeaderTitle
            orderNumber={open ? row?.orderNumber : undefined}
            status={open ? detail?.status : undefined}
          />
        }
        hideFooter={!actionable}
        primaryText={actionable ? 'Approve' : undefined}
        onPrimary={actionable ? () => onApprove?.(actionable) : undefined}
        cancelText={actionable ? 'Reject' : undefined}
        onCancel={actionable ? () => onReject?.(actionable) : undefined}
      >
        {open && row ? (
          <DetailBody row={row} detail={detail} isPending={isPending} onPreview={setPreview} />
        ) : null}
      </Drawer>

      {preview ? (
        <ImageLightbox src={preview} label="Attachment preview" onClose={() => setPreview(null)} />
      ) : null}
    </>
  );
}
